/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { IPurchaseRequisition } from "@/types/purchaseRequisition.type";

type HistoricalItem = IPurchaseRequisition["historical"] extends (infer T)[] ? T : any;

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#333",
  },
  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: "#333",
    marginBottom: 20,
  },
  headerLogo: {
    width: 80,
    height: "auto",
  },
  headerInfo: {
    textAlign: "center",
  },
  companyName: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  companyDetails: {
    fontSize: 9,
    color: "#555",
    marginBottom: 2,
  },
  headerTicket: {
    textAlign: "right",
  },
  ticketNumber: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#e44131", // Cor vermelha do modelo
  },
  ticketDate: {
    fontSize: 9,
  },

  // -- ESTILOS DAS SEÇÕES --
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    paddingBottom: 5,
    marginTop: 15,
    marginBottom: 10,
  },
  detailsContainer: {
    width: "100%",
    marginBottom: 20,
  },
  detailRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  detailColumn: {
    flex: 1,
    flexDirection: "row",
  },
  detailLabel: {
    fontWeight: "bold",
    width: 300,
  },
  detailValue: {},

  // -- ESTILOS DAS TABELAS --
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#ddd",
    marginTop: 15,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f2f2f2",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  tableRow: {
    flexDirection: "row",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  tableRowOdd: {
    // Para linhas alternadas
    flexDirection: "row",
    backgroundColor: "#f9f9f9",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  th: {
    fontWeight: "bold",
    textAlign: "left",
  },
  textCenter: { textAlign: "center" },
  col: { width: "25%" },
  col2: { width: "50%" },

  // -- ESTILOS DO RODAPÉ --
  footer: {
    marginTop: 40,
  },
  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: "#333",
    width: 300,
    marginTop: 60,
    marginBottom: 10,
    textAlign: "center",
    alignSelf: "center", // Para centralizar
  },
  signatureText: {
    marginTop: 5,
  },
});
const translateStatus = (status: string) => {
  const translations: Record<string, string> = {
    APPROVED: "Aprovado",
    IN_PROGRESS: "Em Progresso",
    PENDING: "Pendente",
    REJECTED: "Rejeitado",
    FULLY_QUOTED: "Totalmente Cotado",
    PARTIAL_APPROVED: "Parcialmente Aprovado",
  };
  return translations[status] || status;
};

const formatHistoryAction = (historyItem: HistoricalItem) => {
  const { action, value } = historyItem;

  switch (action) {
    case "create":
      return "Requisição criada";

    case "edit":
      return "Dados da requisição foram editados";

    case "validated":
      return `Validação alterada para: ${translateStatus(value as string)}`;

    case "changeStatus":
      return `Status alterado para: ${translateStatus(value as string)}`;

    default:
      return `${action}: ${value}`;
  }
};
type PurchaseRequisitionPDFProps = {
  data: IPurchaseRequisition;
};

export function PurchaseRequisitionPDF({ data }: PurchaseRequisitionPDFProps) {
  const sortedHistory = Array.isArray(data.historical)
    ? [...data.historical].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
    : [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerTop}>
          <Image
            style={styles.headerLogo}
            src="https://static.wixstatic.com/media/df06c6_6d661cb9805a4bd0b404cddda3f83de8~mv2.png/v1/fill/w_180,h_180,lg_1,usm_0.66_1.00_0.01/df06c6_6d661cb9805a4bd0b404cddda3f83de8~mv2.png"
          />
          <View style={styles.headerInfo}>
            <Text style={styles.companyName}>LOCAVIBE LOCACOES LTDA</Text>
            <Text style={styles.companyDetails}>
              Av. São Paulo, Quadra 19, Lote 03, N° 1651 - Jd. Esmeraldas, GO
            </Text>
            <Text style={styles.companyDetails}>
              CNPJ: 48.012.048/0001-46 | (62) 3003-6199
            </Text>
          </View>
          <View style={styles.headerInfo}>
            <Text style={styles.companyName}>{`Requisição: ${data.code}`}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Informações Gerais</Text>
        <View style={styles.detailsContainer}>
          <View style={styles.detailRow}>
            <View style={{ ...styles.detailColumn, flex: 1.5 }}>
              <Text style={styles.detailLabel}>
                Solicitante: {data.requestorName}
              </Text>
            </View>
            <View style={styles.detailColumn}>
              <Text style={styles.detailLabel}>
                Placa: {data.licensePlate || "N/A"}
              </Text>
            </View>
          </View>
          <View style={styles.detailRow}>
            <View style={{ ...styles.detailColumn, flex: 1.5 }}>
              <Text style={styles.detailLabel}>
                Status: {translateStatus(data.status)}
              </Text>
            </View>
            <View style={styles.detailColumn}>
              <Text style={styles.detailLabel}>
                Prioridade: {data.priority || "Normal"}
              </Text>
            </View>
          </View>
          <View style={styles.detailRow}>
            <View style={{ ...styles.detailColumn, flex: 1.5 }}>
              <Text style={styles.detailLabel}>
                Situação: {translateStatus((data.situation as string) || "")}
              </Text>
            </View>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Itens da Requisição</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={{ ...styles.th, width: "60%" }}>Produto</Text>
            <Text style={{ ...styles.th, width: "20%", textAlign: "center" }}>
              Qtd.
            </Text>
            <Text style={{ ...styles.th, width: "20%" }}>Previsão</Text>
          </View>
          {data.itens.map((item, index) => (
            <View
              key={index}
              style={index % 2 === 1 ? styles.tableRowOdd : styles.tableRow}
            >
              <Text style={{ width: "60%" }}>{item.productName}</Text>
              <Text style={{ width: "20%", textAlign: "center" }}>
                {item.quantity}
              </Text>
              <Text style={{ width: "20%" }}>
                {item.deliveryDate
                  ? new Date(item.deliveryDate).toLocaleDateString("pt-BR")
                  : "N/A"}
              </Text>
            </View>
          ))}
        </View>
        <Text style={styles.sectionTitle}>Histórico de Alterações</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={{ ...styles.th, ...styles.col }}>Data</Text>
            <Text style={{ ...styles.th, ...styles.col }}>Autor</Text>
            <Text style={{ ...styles.th, ...styles.col2 }}>Ação</Text>
          </View>
          {sortedHistory.map((hist, index) => (
            <View
              key={index}
              style={index % 2 === 1 ? styles.tableRowOdd : styles.tableRow}
            >
              <Text style={styles.col}>
                {new Date(hist.date).toLocaleString("pt-BR")}
              </Text>
              <Text style={styles.col}>{hist.author}</Text>
              <Text style={styles.col2}>{formatHistoryAction(hist)}</Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
}
